import React from 'react';
import { motion } from 'framer-motion';
import {
  Wifi,
  Zap,
  ChefHat,
  Shield,
  Tv,
  Wind,
  Lock,
  Sparkles,
  Shirt,
  Package
} from 'lucide-react';

const Amenities = () => {
  const amenitiesList = [
    {
      icon: ChefHat,
      title: 'Fully Equipped Kitchen',
      description: 'Modern appliances including refrigerator, microwave, cooker, and complete cookware for your culinary needs.'
    },
    {
      icon: Zap,
      title: '24-Hour Steady Electricity',
      description: 'Dedicated generator backup ensuring uninterrupted power supply throughout your stay.'
    },
    {
      icon: Wifi,
      title: 'High-Speed Fibre Internet',
      description: 'Complimentary high-speed wireless internet access for seamless connectivity and entertainment.'
    },
    {
      icon: Tv,
      title: 'Smart Flat-Screen TV',
      description: 'Premium entertainment system with multiple channels and streaming capabilities.'
    },
    {
      icon: Lock,
      title: 'In-Room Electronic Safe',
      description: 'Secure storage for your valuables with personal electronic lock system.'
    },
    {
      icon: Wind,
      title: 'Air Conditioning',
      description: 'Individual climate control in every room for your ultimate comfort year-round.'
    },
    {
      icon: Sparkles,
      title: 'Premium Toiletries',
      description: 'Luxury branded bathroom amenities including soaps, shampoos, and body care products.'
    },
    {
      icon: Shirt,
      title: 'Bathrobes & Slippers',
      description: 'Plush bathrobes and comfortable slippers for your relaxation and comfort.'
    },
    {
      icon: Package,
      title: 'Marble Finishing',
      description: 'Elegant marble floors and premium finishes throughout the apartment.'
    },
    {
      icon: Shield,
      title: '24-Hour Security',
      description: 'Round-the-clock professional security personnel and CCTV surveillance for your safety.'
    }
  ];

  return (
    <div data-testid="amenities-page" className="min-h-screen bg-[#FAF8F5] pt-24">
      {/* Header */}
      <section className="py-16 px-6 md:px-12 lg:px-24 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          data-testid="amenities-page-title"
          className="font-serif font-bold tracking-tighter leading-none text-[#1E3A8A] mb-6"
          style={{ fontSize: '26px' }}
        >
          Amenities
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="amenities-page-subtitle"
          className="text-base leading-relaxed text-[#3A3A3A] max-w-3xl mx-auto"
        >
          Every suite is thoughtfully equipped with premium amenities designed to make your stay exceptional.
        </motion.p>
      </section>

      {/* Amenities Grid */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {amenitiesList.map((amenity, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`amenity-item-${index}`}
              className="bg-white border border-[#E5E5E5] p-8 hover:border-[#C9A84C] transition-colors duration-500"
            >
              <amenity.icon
                className="text-[#C9A84C] mb-4"
                size={36}
                strokeWidth={1}
              />
              <h3 className="text-xl font-medium text-[#1A1A1A] mb-3">{amenity.title}</h3>
              <p className="text-base leading-relaxed text-[#3A3A3A]">{amenity.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 text-center bg-[#F2EBE1]">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2
            data-testid="amenities-cta-title"
            className="font-serif text-3xl sm:text-4xl tracking-tight font-light text-[#1A1A1A] mb-8"
          >
            Experience Luxury Living
          </h2>
          <a
            href="/book"
            data-testid="amenities-book-btn"
            className="inline-block bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-12 py-4 text-sm tracking-widest uppercase"
          >
            Reserve Your Suite
          </a>
        </motion.div>
      </section>
    </div>
  );
};

export default Amenities;